import React, { useEffect, useMemo, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";

interface TeamAvailabilityCalendarProps {
    selectedDates: { date: Date; isHalfDay: boolean }[];
    onDateClick: (date: Date, totalAllocatedHours: any) => void;
    startDate?: Date | null;
    endDate?: Date | null;
    timeData?: any;
}

const TeamAvailabilityCalendar: React.FC<TeamAvailabilityCalendarProps> = ({
    selectedDates,
    onDateClick,
    startDate,
    endDate,
    timeData
}) => {
    const today = new Date();
    // --- keep calendar's current visible month in state ---
    const [month, setMonth] = useState<Date>(today);

    // Update visible month when startDate or endDate change
    useEffect(() => {
        if (startDate) {
            setMonth(new Date(startDate));
        } else {
            setMonth(today);
        }
    }, [startDate, endDate]);

    // Compute boundaries
    const { fromMonth, toMonth, disabledDays, calendarDisabled } = useMemo(() => {
        // Case 1: if startDate or endDate missing -> disable all dates
        if (!startDate || !endDate) {
            return {
                fromMonth: today,
                toMonth: today,
                disabledDays: [{ before: new Date(9999, 11, 31) }], // disable all
                calendarDisabled: true
            };
        }

        // Case 2: valid start and end range
        const from = new Date(startDate);
        const to = new Date(endDate);

        return {
            fromMonth: from,
            toMonth: to,
            disabledDays: [
                { before: from },
                { after: to },
                { dayOfWeek: [0, 6] } // disable weekends
            ],
            calendarDisabled: false
        };
    }, [startDate, endDate]);

    // --- Default selected date ---
    const selectedDate = useMemo(() => {
        if (!startDate || !endDate) return undefined;
        return selectedDates.length > 0
            ? selectedDates[0].date
            : new Date(startDate);
    }, [selectedDates, startDate, endDate]);

    // --- Compute day status modifiers ---
    const { availableDays, partialDays, unavailableDays } = useMemo(() => {
        const result = {
            availableDays: [] as Date[],
            partialDays: [] as Date[],
            unavailableDays: [] as Date[],
        };

        if (timeData?.data?.days?.length) {
            for (const d of timeData.data.days) {
                const date = new Date(d.date);
                 const dayOfWeek = date.getDay();

                // Skip weekends
                if (dayOfWeek === 0 || dayOfWeek === 6) continue;
                const remaining = 8 - (d.day_total_allocated_hours || 0);

                // If person is on leave, mark as unavailable regardless of hours
                if (d.on_leave) {
                    result.unavailableDays.push(date);
                    continue;
                }

                if (remaining === 8) {
                    result.availableDays.push(date);
                } else if (remaining > 0 && remaining < 8) {
                    result.partialDays.push(date);
                } else if (remaining <= 0) {
                    result.unavailableDays.push(date);
                }
            }
        }

        return result;
    }, [timeData]);

    // --- Trigger onDateClick automatically when both dates exist and parent has none ---
    useEffect(() => {
        if (startDate && endDate && selectedDates.length === 0) {
            // Try to find matching day data from timeData
            const matchedDay = timeData?.data?.days?.find(
                (d: any) => new Date(d.date).toDateString() === new Date(startDate).toDateString()
            );

            const totalAllocatedHours = matchedDay?.day_total_allocated_hours ?? null;

            onDateClick(new Date(startDate), totalAllocatedHours);
        }
    }, [startDate, endDate]); // Only trigger when range changes

    return (
        <div className=" bg-white responsive-datepicker team-responsive-datepicker w-full teamavaibility_dv">
            <div className="flex gap-[16px] justify-between mb-5 mq575:grid">
                <h2 className="font-semibold mb-2 mt-0">Team Availability</h2>
                <div className="flex gap-[16px] status-avai-main mq575:contents">
                    <div className="availability-status-dv">
                        <span className="status-available w-5 h-5"></span>
                        <span>Available</span>
                    </div>
                    <div className="availability-status-dv">
                        <span className="status-partial w-5 h-5"></span>
                        <span>Partial</span>
                    </div>
                    <div className="availability-status-dv">
                        <span className="status-unavailable w-5 h-5"></span>
                        <span>Unavailable</span>
                    </div>
                </div>
            </div>

            <DayPicker
                key={`${startDate?.toISOString() || "no-start"}-${endDate?.toISOString() || "no-end"}`}
                mode="single"
                selected={selectedDate}
                onDayClick={day => {
                    if (!calendarDisabled) {
                        // Find matching date in timeData
                        const matchedDay = timeData?.data?.days?.find(
                            (d: any) => new Date(d.date).toDateString() === day.toDateString()
                        );
                        const totalAllocatedHours = matchedDay?.day_total_allocated_hours ?? null;

                        onDateClick(day, totalAllocatedHours);
                    }
                }}
                month={month}              // controlled month
                onMonthChange={setMonth}   // keep state in sync
                // defaultMonth={defaultMonth}
                fromMonth={fromMonth}
                toMonth={toMonth}
                disabled={disabledDays} // Can't select past dates

                modifiers={{
                    available: availableDays,
                    partial: partialDays,
                    unavailable: unavailableDays,
                }}
                modifiersClassNames={{
                    available: "status-available-day",
                    partial: "status-partial-day",
                    unavailable: "status-unavailable-day",
                }}
            />
        </div>
    );
};

export default TeamAvailabilityCalendar;
